<template>
  <button
    type="button"
    :tabindex="String(tabindex)"
    class="inline-flex cursor-pointer touch-manipulation items-center justify-center space-x-1 rounded-md border select-none disabled:cursor-not-allowed! has-[.active-indicator-badge]:space-x-0"
    :class="[variantClass, sizeClass, colorClass]"
    ref="button"
  >
    <FontAwesomeIcon v-if="icon" :icon :size="sizeButtonClass" :class="animateButtonClass" />
    <slot />
  </button>
</template>

<script setup lang="ts">
import { computed, ref } from "vue"
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"

const props = defineProps({
  /**
   * The buttons tab index
   */
  tabindex: {
    type: [String, Number],
    default: "0"
  },
  /**
   * The button's variant
   */
  variant: {
    type: String,
    default: "flat",
    validator: (value: string) => {
      return ["elevated", "flat", "outlined", "transparent", "text", "plain"].includes(value)
    }
  },
  /**
   * The button's size
   */
  size: {
    type: String,
    default: "medium",
    validator: (value: string) => {
      return ["small", "medium", "large"].includes(value)
    }
  },
  /**
   * The button's animaton
   */
  animate: {
    type: String,
    validator: (value: string) => {
      return ["spin", "ping", "pulse", "bounce"].includes(value)
    }
  },
  /**
   * The button's color
   */
  color: {
    type: String,
    default: "primary",
    validator: (value: string) => {
      return [
        "primary",
        "primary-lighten-1",
        "secondary",
        "surface",
        "info",
        "warning",
        "error",
        "success"
      ].includes(value)
    }
  },
  icon: {
    type: Array
  }
})

// This will ensure tailwind will render the classes
const backgroundClasses: Record<string, string> = {
  primary:
    "bg-primary dark:bg-primary-dark disabled:bg-primary/50 dark:disabled:bg-primary-dark/50",
  "primary-lighten-1":
    "bg-primary-lighten-1 dark:bg-primary-lighten-1-dark disabled:bg-primary-lighten-1/50 dark:disabled:bg-primary-lighten-1-dark/50",
  secondary:
    "bg-secondary dark:bg-secondary-dark disabled:bg-secondary/50 dark:disabled:bg-secondary-dark/50",
  surface:
    "bg-surface dark:bg-surface-dark disabled:bg-surface/50 dark:disabled:bg-surface-dark/50",
  info: "bg-info dark:bg-info-dark disabled:bg-info/50 dark:disabled:bg-info-dark/50",
  warning:
    "bg-warning dark:bg-warning-dark disabled:bg-warning/50 dark:disabled:bg-warning-dark/50",
  error: "bg-error dark:bg-error-dark disabled:bg-error/50 dark:disabled:bg-error-dark/50",
  success: "bg-success dark:bg-success-dark disabled:bg-success/50 dark:disabled:bg-success-dark/50"
}

const textOnBackgroundClasses: Record<string, string> = {
  primary:
    "text-on-primary dark:text-on-primary-dark disabled:text-on-primary/50 dark:disabled:text-on-primary-dark/50",
  "primary-lighten-1":
    "text-on-primary-lighten-1 dark:text-on-primary-lighten-1-dark disabled:text-on-primary-lighten-1/50 dark:disabled:text-on-primary-lighten-1-dark/50",
  secondary:
    "text-on-secondary dark:text-on-secondary-dark disabled:text-on-secondary/50 dark:disabled:text-on-secondary-dark/50",
  surface:
    "text-on-surface dark:text-on-surface-dark disabled:text-on-surface/50 dark:disabled:text-on-surface-dark/50",
  info: "text-on-info dark:text-on-info-dark disabled:text-on-info/50 dark:disabled:text-on-info-dark/50",
  warning:
    "text-on-warning dark:text-on-warning-dark disabled:text-on-warning/50 dark:disabled:text-on-warning-dark/50",
  error:
    "text-on-error dark:text-on-error-dark disabled:text-on-error/50 dark:disabled:text-on-error-dark/50",
  success:
    "text-on-success dark:text-on-success-dark disabled:text-on-success/50 dark:disabled:text-on-success-dark/50"
}

const textColorClasses: Record<string, string> = {
  primary:
    "text-primary dark:text-primary-dark disabled:text-primary/50 dark:disabled:text-primary-dark/50",
  "primary-lighten-1":
    "text-primary-lighten-1 dark:text-primary-lighten-1-dark disabled:text-primary-lighten-1/50 dark:disabled:text-primary-lighten-1-dark/50",
  secondary:
    "text-secondary dark:text-secondary-dark disabled:text-secondary/50 dark:disabled:text-secondary-dark/50",
  surface:
    "text-surface dark:text-surface-dark disabled:text-surface/50 dark:disabled:text-surface-dark/50",
  info: "text-info dark:text-info-dark disabled:text-info/50 dark:disabled:text-info-dark/50",
  warning:
    "text-warning dark:text-warning-dark disabled:text-warning/50 dark:disabled:text-warning-dark/50",
  error: "text-error dark:text-error-dark disabled:text-error/50 dark:disabled:text-error-dark/50",
  success:
    "text-success dark:text-success-dark disabled:text-success/50 dark:disabled:text-success-dark/50"
}

const borderClasses: Record<string, string> = {
  primary:
    "border-primary dark:border-primary-dark disabled:border-primary/50 dark:disabled:border-primary-dark/50",
  "primary-lighten-1":
    "border-primary-lighten-1 dark:border-primary-lighten-1-dark disabled:border-primary-lighten-1/50 dark:disabled:border-primary-lighten-1-dark/50",
  secondary:
    "border-secondary dark:border-secondary-dark disabled:border-secondary/50 dark:disabled:border-secondary-dark/50",
  surface:
    "border-surface dark:border-surface-dark disabled:border-surface/50 dark:disabled:border-surface-dark/50",
  info: "border-info dark:border-info-dark disabled:border-info/50 dark:disabled:border-info-dark/50",
  warning:
    "border-warning dark:border-warning-dark disabled:border-warning/50 dark:disabled:border-warning-dark/50",
  error:
    "border-error dark:border-error-dark disabled:border-error/50 dark:disabled:border-error-dark/50",
  success:
    "border-success dark:border-success-dark disabled:border-success/50 dark:disabled:border-success-dark/50"
}

const focusClasses: Record<string, string> = {
  primary: "focus:outline focus:outline-secondary",
  "primary-lighten-1": "focus:outline focus:outline-secondary",
  secondary: "focus:outline focus:outline-surface",
  surface: "focus:outline focus:outline-secondary",
  info: "focus:outline focus:outline-secondary",
  warning: "focus:outline focus:outline-primary",
  error: "focus:outline focus:outline-secondary",
  success: "focus:outline focus:outline-secondary"
}

const variantClass = computed(() => {
  switch (props.variant) {
    /** Elevates the button with a shadow */
    case "elevated":
      return "border-transparent shadow-md"
    /** Applies a thin border with the current text color */
    case "outlined":
      return "border-current"
    /** Removes the background and removes shadow */
    case "text":
      return "border-transparent"
    /** Removes the background and lowers the opacity until hovered */
    case "plain":
      return "border-transparent opacity-50 hover:opacity-100"
    /** Default */
    case "flat":
    default:
      return "border-transparent"
  }
})

const sizeClass = computed(() => {
  switch (props.size) {
    case "small":
      return "p-0.5 min-h-5 font px-1 text-xs"
    case "large":
      return "p-3 min-h-12 text-base"
    case "medium":
    default:
      return "p-2 text-sm"
  }
})

const sizeButtonClass = computed(() => {
  switch (props.size) {
    case "small":
      return "xs"
    case "large":
      return "lg"
    case "medium":
    default:
      return undefined
  }
})

const animateButtonClass = computed(() => {
  switch (props.animate) {
    case "spin":
      return "animate-spin"
    case "ping":
      return "animate-ping"
    case "pulse":
      return "animate-pulse"
    case "bounce":
      return "animate-bounce"
    default:
      return undefined
  }
})

const colorClass = computed(() => {
  const baseColorClass = props.color || "primary"

  if (props.variant === "outlined") {
    return `${borderClasses[baseColorClass]} ${textColorClasses[baseColorClass]} ${focusClasses[baseColorClass]}`
  } else if (props.variant === "text" || props.variant === "plain") {
    return `${textColorClasses[baseColorClass]} ${focusClasses[baseColorClass]}`
  } else if (props.variant === "transparent") {
    return `${textOnBackgroundClasses[baseColorClass]} ${focusClasses[baseColorClass]}`
  } else {
    return `${backgroundClasses[baseColorClass]} ${textOnBackgroundClasses[baseColorClass]} ${focusClasses[baseColorClass]}`
  }
})

const button = ref()
function focus() {
  if (button.value) {
    button.value.focus()
  }
}

function click() {
  if (button.value) {
    button.value.click()
  }
}

defineExpose({
  focus,
  click
})
</script>

<style scoped></style>
